<template>
  <p-container id="callLogDetails">
    <p-row column>
      <div>
        <p-row align-center>
          <p-button tertiary @click="$router.back()">
            <p-icon>
              keyboard_backspace
            </p-icon>
          </p-button>
          <h2>{{ $t('core.page.callLog.callLogDetail.title') }} {{ callLogId }}</h2>
        </p-row>
      </div>
      <p-card>
        <p-row column>
          <p-col>
            <p-row column dense>
              <div>
                <table class="requestIdentityDetails">
                  <tr>
                    <th class="id">
                      Id:
                    </th>
                    <th class="correlationId">
                      {{ $t('core.page.callLog.list.correlationId') }}:
                    </th>
                    <th class="targetSystemName">
                      {{ $t('core.page.callLog.callLogDetail.detail.targetSystemName') }}:
                    </th>
                  </tr>
                  <tr>
                    <td>{{ callLogDetail.id }}</td>
                    <td>{{ callLogDetail.correlationId }}</td>
                    <td>{{ callLogDetail.targetSystemName }}</td>
                  </tr>
                </table>
              </div>
              <div>
                <h3>Request</h3>
                {{ callLogDetail.requestType }} - {{ callLogDetail.requestPath }}
              </div>
              <div>
                <table class="requestDurationAndStatusDetails">
                  <tr>
                    <th class="requestTime">
                      {{ $t('core.page.callLog.list.requestTime') }}:
                    </th>
                    <th class="responseTime">
                      {{ $t('core.page.callLog.callLogDetail.detail.responseTime') }}:
                    </th>
                    <th class="responseDuration">
                      {{ $t('core.page.callLog.callLogDetail.detail.responseDuration') }}:
                    </th>
                    <th class="responseStatus">
                      {{ $t('core.page.callLog.list.responseStatus') }}:
                    </th>
                    <th>
                      Message:
                    </th>
                  </tr>
                  <tr>
                    <td>
                      <span v-if="$date(callLogDetail.requestTime)">{{ $date(callLogDetail.requestTime).format('DD.MM.YYYY HH:mm:ss:SSS') }}</span>
                    </td>
                    <td>
                      <span v-if="$date(callLogDetail.responseTime)">{{ $date(callLogDetail.responseTime).format('DD.MM.YYYY HH:mm:ss:SSS') }}</span>
                    </td>
                    <td>{{ callLogDetail.responseDuration }}</td>
                    <td>
                      <p-pill :color="`${statusColor}-lighten-3`">
                        <p-badge no-outline center dense :color="statusColor"></p-badge>
                        <span class="ml-5">
                          {{ callLogDetail.responseStatusCode }}
                        </span>
                      </p-pill>
                    </td>
                    <td>
                      {{ callLogDetail.responseInfo }}
                    </td>
                  </tr>
                </table>
              </div>
              <strong>{{ $t('core.page.callLog.callLogDetail.detail.requestUrl') }}:</strong>
              <div>
                {{ callLogDetail.requestUrl }}
              </div>
              <div v-if="callLogDetail.exceptionMessage">
                {{ $t('core.page.callLog.callLogDetail.detail.exceptionMessage') }}: {{ callLogDetail.exceptionMessage }}
              </div>
            </p-row>
          </p-col>
        </p-row>
      </p-card>
      <p-row>
        <p-col xs12 sm12 lg12>
          <p-card>
            <p-row column>
              <p-accordion>
                <!--v-model="objectJsonExpanded"-->
                <template #expander="{ expanded }">
                  <p-row align-center>
                    <h3>{{ $t('core.page.callLog.list.requestBody') }}</h3>
                    <p-icon v-if="expanded">
                      expand_less
                    </p-icon>
                    <p-icon v-else>
                      expand_more
                    </p-icon>
                  </p-row>
                </template>
                <p-code v-if="isRequestBodyValid" lang="json">
                  {{ callLogDetail.requestBody }}
                </p-code>
                <div v-else>
                  <p-alert :value="true" type="info">
                    {{ $t('core.page.callLog.callLogDetail.requestBody.errorMessage') }}
                  </p-alert>
                  <br>
                  <p-code>
                    {{ callLogDetail.requestBody }}
                  </p-code>
                </div>
              </p-accordion>
            </p-row>
          </p-card>
        </p-col>
        <p-col xs12 sm12 lg12>
          <p-card>
            <p-row column>
              <p-accordion>
                <!--v-model="objectJsonExpanded"-->
                <template #expander="{ expanded }">
                  <p-row align-center>
                    <h3>{{ $t('core.page.callLog.list.responseBody') }}</h3>
                    <p-icon v-if="expanded">
                      expand_less
                    </p-icon>
                    <p-icon v-else>
                      expand_more
                    </p-icon>
                  </p-row>
                </template>
                <p-code v-if="isResponseBodyValid" lang="json">
                  {{ callLogDetail.responseBody }}
                </p-code>
                <div v-else>
                  <p-alert :value="true" type="info">
                    {{ $t('core.page.callLog.callLogDetail.requestBody.errorMessage') }}
                  </p-alert>
                  <br>
                  <p-code>
                    {{ callLogDetail.responseBody }}
                  </p-code>
                </div>
              </p-accordion>
            </p-row>
          </p-card>
        </p-col>
      </p-row>
    </p-row>
  </p-container>
</template>

<script lang="ts">
  import Vue from 'vue';
  import { getStatusType, StatusType } from '../../../../plugins/servicelayer/servicelayer';
  import CallLogWithDurationResourceViewModel from '../../../../services/v1/viewModel/resource/CallLogWithDurationResourceViewModel';

  export default Vue.extend({
    auth: true,
    role: ['admin', 'hostadmin'],
    layout: 'layout-hostadmin',
    data: () => ({
      callLogDetail: {} as CallLogWithDurationResourceViewModel,
      isRequestBodyValid: true,
      isResponseBodyValid: true,
    }),
    computed: {
      callLogId() {
        const idParam = this.$router.currentRoute.params.id;
        return Number.parseInt(idParam, 10);
      },
      statusColor(): string {
        const statusType = getStatusType(this.callLogDetail?.responseStatusCode);
        switch (statusType) {
        case StatusType.ClientError:
        case StatusType.ServerError:
        case StatusType.Unknown:
          return 'danger';
        case StatusType.Informational:
          return 'info';
        case StatusType.Redirection:
          return 'warning';
        case StatusType.Success:
          return 'success';
        default:
          return 'disabled';
        }
      },
    },
    async mounted() {
      await this.populateData();
    },
    methods: {
      async populateData() {
        const responseCallLog = await this.$service.api.core.callLogs.getCallLogById(this.callLogId);
        // TODO: Parse JSON and set isRequestBodyTruncated flag
        this.callLogDetail = responseCallLog;
        this.callLogDetail.requestBody = await this.loadRequestBody();
        this.callLogDetail.responseBody = await this.loadResponseBody();
      },
      async loadRequestBody() {
        let callLogRequestBody = this.callLogDetail.requestBody ?? 'undefined';
        try {
          callLogRequestBody = JSON.stringify(JSON.parse(callLogRequestBody), undefined, 2);
          return callLogRequestBody;
        } catch {
          this.isRequestBodyValid = false;
          return callLogRequestBody;
        }
      },
      async loadResponseBody() {
        let callLogResponseBody = this.callLogDetail.responseBody ?? 'undefined';
        try {
          callLogResponseBody = JSON.stringify(JSON.parse(callLogResponseBody), undefined, 2);
          return callLogResponseBody;
        } catch {
          this.isResponseBodyValid = false;
          return callLogResponseBody;
        }
      },

    },
  });
</script>
<style>
  #callLogDetails .p-code {
    width: calc(100% - 24px);
    white-space: pre-wrap;
    word-break: break-all;
  }
  #callLogDetails .p-alert {
    width: calc(100% - 24px);
  }
  #callLogDetails th{
    text-align: left;
    font-size: bold;
  }
  #callLogDetails .requestTime, #callLogDetails .responseTime, #callLogDetails .id, #callLogDetails .correlationId{
    width: 190px;
  }
  #callLogDetails .responseDuration{
    width: 110px;
  }
  #callLogDetails .responseStatus{
    width: 70px;
  }
</style>
